import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelectedAppStore } from '../../store/useSelectedAppStore';
import './LeftMenu.css';

// Left Navigation Menu - Common for all applications
// Simple, non-technical naming for no-code users
const LeftMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedApp, getSelectedAppPrefix } = useSelectedAppStore();

  const menuItems = [
    {
      id: 'dashboard',
      icon: '🏠',
      label: 'Dashboard',
      path: '/dashboard',
      description: 'Your control center to see everything at a glance.'
    },
    {
      id: 'actions',
      icon: '🎬',
      label: 'Actions',
      path: '/actions',
      requiresApp: true,
      description: 'Configure actions for buttons, forms, and interactions.'
    },
    {
      id: 'pages',
      icon: '📄',
      label: 'Pages',
      path: '/page-builder',
      requiresApp: true,
      description: 'Design screens your users will see (forms, lists, dashboards).'
    },
    {
      id: 'app-data',
      icon: '📊',
      label: 'App Data',
      path: '/data-management',
      requiresApp: true,
      description: 'Upload or manage data like users, products, or records using files or tables.'
    },
    {
      id: 'assets',
      icon: '🎨',
      label: 'Assets',
      path: '/assets',
      requiresApp: true,
      description: 'Store images, files, and media used across your application.'
    },
    {
      id: 'logic',
      icon: '⚙',
      label: 'Business Logic',
      path: '/validations',
      requiresApp: true,
      description: 'Define rules like validations, conditions, and calculations (no coding).'
    },
    {
      id: 'processes',
      icon: '🔄',
      label: 'Processes',
      path: '/dashboard',
      requiresApp: true,
      description: 'Create step-by-step flows such as approvals or automations.',
      customHandler: (navigate, selectedApp) => {
        if (selectedApp) {
          const appName = selectedApp.appName || selectedApp.name;
          navigate(`/dashboard/${encodeURIComponent(appName)}/processes`);
        }
      }
    },
    {
      id: 'integrations',
      icon: '🔌',
      label: 'Integrations',
      path: '/integrations',
      requiresApp: true,
      description: 'Connect your app with external tools like email, payments, or APIs.'
    },
    {
      id: 'manage-users',
      icon: '👥',
      label: 'Manage users',
      path: '/manage-users',
      requiresApp: true,
      description: 'Manage user accounts, permissions, and access control.'
    },
    {
      id: 'analytics',
      icon: '📊',
      label: 'Analytics',
      path: '/analytics',
      requiresApp: true,
      description: 'Understand how your app is being used.'
    },
    {
      id: 'billing',
      icon: '💳',
      label: 'Billing & Usage',
      path: '/billing',
      requiresApp: false,
      description: 'View your plan, usage limits, and invoices.'
    },
    {
      id: 'settings',
      icon: '⚙',
      label: 'Settings',
      path: '/settings',
      requiresApp: false,
      description: 'Configure branding, environment, and platform preferences.'
    }
  ];

  const handleMenuClick = (item) => {
    if (item.comingSoon) {
      return; // Don't navigate for coming soon items
    }

    if (item.requiresApp && !selectedApp) {
      // Show message that app needs to be selected
      alert('Please select an application first from the dropdown in the top navigation.');
      return;
    }

    // Use custom handler if provided
    if (item.customHandler) {
      item.customHandler(navigate, selectedApp);
      return;
    }

    let path = item.path;
    
    // Add app parameter if required
    if (item.requiresApp && selectedApp) {
      const appName = selectedApp.appName || selectedApp.name;
      const separator = path.includes('?') ? '&' : '?';
      path = `${path}${separator}app=${encodeURIComponent(appName)}`;
    }

    navigate(path);
  };

  const isActive = (item) => {
    // Special handling for dashboard
    if (item.id === 'dashboard' && location.pathname === '/dashboard') {
      return true;
    }
    // Special handling for processes
    if (item.id === 'processes' && location.pathname.includes('/processes')) {
      return true;
    }
    // For other items, check if path matches
    if (item.path !== '/dashboard') {
      return location.pathname.startsWith(item.path);
    }
    return false;
  };

  return (
    <nav className="left-menu">
      <div className="left-menu-content">
        {menuItems.map((item) => {
          const active = isActive(item);
          const disabled = item.requiresApp && !selectedApp;
          
          return (
            <div
              key={item.id}
              className={`left-menu-item ${active ? 'active' : ''} ${disabled ? 'disabled' : ''} ${item.comingSoon ? 'coming-soon' : ''}`}
              onClick={() => handleMenuClick(item)}
              title={item.description}
            >
              <span className="menu-item-icon">{item.icon}</span>
              <span className="menu-item-label">{item.label}</span>
              {item.comingSoon && (
                <span className="coming-soon-badge">🚧 Coming Soon</span>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
};

export default LeftMenu;

